import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { HttpException } from '../../exceptions/index';
import { ApiService } from '../../services/index';
import { SafeOrder, SafeSortBy } from '../../types/safes.types';
import { useAppQuery } from '../use-app-query.hook';
import { GetSafesResponse } from './use-safes';

export const useStakeholderSafes = ({
  companyId,
  stakeholderId,
  numberOfFetchedItems = 10,
  sortBy,
  currentPage,
  search,
  sortOrder,
  convertedOnly,
}: {
  companyId: string;
  convertedOnly?: boolean;
  currentPage: number;
  numberOfFetchedItems?: number;
  search: string;
  sortBy: null | SafeSortBy;
  sortOrder: null | SafeOrder;
  stakeholderId: string;
}) => {
  const queryClient = useQueryClient();
  const skip = (currentPage - 1 <= 0 ? 0 : currentPage - 1) * numberOfFetchedItems;
  const {
    data: { data = [], totalPages, totalSafesCount } = {},
    isLoading,
    refetch,
  } = useAppQuery<GetSafesResponse, HttpException>({
    queryKey: [
      QueryKey.GET_STAKEHOLDER_SAFES,
      companyId,
      stakeholderId,
      {
        page: currentPage,
        sortBy,
        sortOrder,
        skip,
        numberOfFetchedItems,
        convertedOnly,
      },
    ],
    queryFn: () =>
      ApiService.get(
        {
          endpoint: BackendRoute.COMPANIES,
          routePath: [companyId, 'stakeholders', stakeholderId, 'safes'],
        },
        {
          queryParams: {
            take: numberOfFetchedItems,
            skip,
            search,
            convertedOnly: convertedOnly,
            ...(sortOrder && sortBy ? { sortBy, sortOrder } : {}),
          },
        },
      ),
    enabled: !!companyId && !!stakeholderId,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_STAKEHOLDER_SAFES] });
  }, [queryClient]);

  return {
    invalidateQuery,
    safes: data,
    totalPages,
    totalSafesCount,
    isLoading,
    refetch,
  };
};
